import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import AppContainer from "../Structure/AppContainer";
import AuthContext from "../../Context/AuthContext";
import ajaxRequisitions from "../../util/remote/ajaxRequisitions";
import useStateCallback from "../../util/customHooks/useStateCallback";
import RequistionUpdateApprovalLevel from "./RequistionUpdateApprovalLevel";
const currency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "UGX",
  maximumFractionDigits: 0,
});
const formatMoney = (v) => (v ? currency.format(Number(v)) : "—");
const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const StatusBadge = ({ status, level }) => {
  if (status === "10") {
    return (
      <span className="badge bg-success d-inline-flex align-items-center gap-2">
        <i className="fa fa-check-square" aria-hidden="true" />
        Approved
      </span>
    );
  }
  return (
    <span className="badge bg-warning text-dark">
      {status} / {level} Approved
    </span>
  );
};
const ViewRequisitionsToSetApprovalLevels = () => {
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [approval_level, setApproval_level] = useStateCallback(false);
  // UI state: filters
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | APPROVED | PENDING
  const fetchRequisitionsList = async () => {
    setLoading(true);
    setRequisitions([]);
    const server_response =
      await ajaxRequisitions.fetchRequisitionsTosetApprovelslevel({});
    setLoading(false);
    if (server_response.status === "OK") {
      setRequisitions(server_response.details || []);
    } else {
      toast.error(server_response.message || "Failed to load requisitions");
    }
  };
  useEffect(() => {
    fetchRequisitionsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleUpdateApproval = (data) => {
    setApproval_level(false, () =>
      setApproval_level(
        <RequistionUpdateApprovalLevel
          data={data}
          funct={fetchRequisitionsList}
        />
      )
    );
  };
  const filtered = useMemo(() => {
    return (requisitions || []).filter((r) => {
      if (statusFilter === "APPROVED" && r.status !== "10") return false;
      if (statusFilter === "PENDING" && r.status === "10") return false;
      return true;
    });
  }, [requisitions, statusFilter]);

  const approvedCount = useMemo(
    () => requisitions.filter((r) => r.status === "10").length,
    [requisitions]
  );
  return (
    <AppContainer title="Requisitions View">
      <Toaster />
      {/* Page header + actions */}
      <div className="container-fluid">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h5 className="mb-0">Organisation Requisitions</h5>
            <small className="text-muted">
              Manage approval levels with ease
            </small>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchRequisitionsList}
              disabled={loading}
              title="Refresh"
            >
              <i
                className={`fa ${
                  loading ? "fa-spinner fa-spin" : "fa-rotate-right"
                }`}
              />{" "}
              Refresh
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/requsition/create")}
            >
              <i className="fa fa-plus-circle me-1" />
              Create Requisition
            </button>
          </div>
        </div>
        {/* Quick stats */}
        <div className="row g-3 mb-3">
          <div className="col-sm-6 col-md-4 col-lg-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">Total</div>
                    <div className="h5 mb-0">{requisitions.length}</div>
                  </div>
                  <i className="fa fa-list-ul fs-3 text-secondary" />
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-md-4 col-lg-3">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">Approved</div>
                    <div className="h5 mb-0">{approvedCount}</div>
                  </div>
                  <i className="fa fa-check-circle fs-3 text-success" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="ALL">All statuses</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Table Card */}
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div
              className="table-responsive"
              style={{ maxHeight: 520, overflow: "auto" }}
            >
              <table className="table table-striped table-hover align-middle mb-0">
                <thead
                  className="table-light sticky-top"
                  style={{ top: 0, zIndex: 1 }}
                >
                  <tr>
                    <th className="text-nowrap">#</th>
                    <th className="text-nowrap">Narration</th>
                    <th className="text-nowrap">Department</th>
                    <th className="text-nowrap">Est. Total</th>
                    <th className="text-nowrap">Approval Level</th>
                    <th className="text-nowrap">Status</th>
                    <th className="text-nowrap">Date</th>
                    <th className="text-nowrap text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? (
                    filtered.map((req, idx) => (
                      <tr key={`${req.id || idx}`}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{req.naration || "—"}</td>
                        <td>{req.department || "—"}</td>
                        <td>{formatMoney(req.est_total)}</td>
                        <td>
                          <span className="badge bg-info text-dark">
                            {req.approval_level || 0}
                          </span>
                        </td>
                        <td>
                          <StatusBadge
                            status={req.status}
                            level={req.approval_level}
                          />
                        </td>
                        <td>{formatDate(req.date_created)}</td>
                        <td className="text-end">
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-warning"
                              disabled={req.status === "10"}
                              onClick={() => handleUpdateApproval(req)}
                              title="Update Approval Level"
                            >
                              <i className="fa fa-pencil me-1" />
                              Approval_level
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="py-5 text-center text-muted">
                          <i className="fa fa-inbox fa-2x mb-3 d-block" />
                          <div className="fw-semibold">
                            No requisitions to display
                          </div>
                          <div className="small">
                            Try adjusting your filters or create a new
                            requisition.
                          </div>
                          <button
                            className="btn btn-sm btn-primary mt-3"
                            onClick={() => navigate("/requsition/create")}
                          >
                            <i className="fa fa-plus me-1" /> Create Requisition
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Loading overlay */}
            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  background: "rgba(255,255,255,0.6)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <div className="text-center">
                  <div className="spinner-border" role="status" />
                  <div className="mt-2 small text-muted">
                    Loading requisitions…
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Modals */}
        {approval_level}
      </div>
    </AppContainer>
  );
};
export default ViewRequisitionsToSetApprovalLevels;
